import { datasetSchemaForCanonical } from './datasetMetadata.ts'

export const SITE_URL = 'https://www.omikina.com'

export interface SocialCard {
  url: string
  alt: string
  type?: string
  width?: number
  height?: number
}

export const DEFAULT_SOCIAL_CARD: SocialCard = {
  url: `${SITE_URL}/social/omikina-x-share-v3.png`,
  alt: 'OMIKINA — The AI Race, Now. Source-linked global AI news, industry impact, and infrastructure.',
  type: 'image/png', width: 1200, height: 630,
}

export function isEditionDate(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const time = Date.parse(`${value}T12:00:00Z`)
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value
}

export function canonicalPagePath(path: string, search = ''): string {
  const normalized = path.replace(/\/$/, '') || '/'
  const edition = new URLSearchParams(search).get('edition')
  if (isEditionDate(edition)) {
    if (normalized === '/intelligence') return `/briefing/${edition}`
    if (normalized === '/intelligence/state-of-ai') return `/intelligence/state-of-ai/${edition}`
  }
  return normalized
}

export function publicationMetadataDates(record: { publishedAt?: string; generatedAt?: string; revisedAt?: string }) {
  const valid = (value: string | undefined): value is string => Boolean(value && Number.isFinite(Date.parse(value)))
  const datePublished = valid(record.publishedAt) ? record.publishedAt : valid(record.generatedAt) ? record.generatedAt : undefined
  const dateModified = datePublished && valid(record.revisedAt) && Date.parse(record.revisedAt) >= Date.parse(datePublished) ? record.revisedAt : undefined
  return { datePublished, dateModified }
}

export function socialCardForPath(path: string): SocialCard {
  if (path === '/intelligence' || path.startsWith('/briefing/')) return {
    ...DEFAULT_SOCIAL_CARD, url: `${SITE_URL}/social/omikina-five-changes-share.png`,
    alt: 'OMIKINA — Five Changes That Matter. A source-linked daily AI briefing.',
  }
  if (path.startsWith('/intelligence/state-of-ai')) return {
    ...DEFAULT_SOCIAL_CARD, url: `${SITE_URL}/social/omikina-state-of-ai-share.png`,
    alt: 'OMIKINA — The State of AI. A daily full-corpus source-linked synthesis.',
  }
  return DEFAULT_SOCIAL_CARD
}

export interface PageMetadata {
  canonical: string
  title: string
  description: string
  robots: string
  socialCard: SocialCard
  type?: string
  datePublished?: string
  dateModified?: string
  author?: string
  authorUrl?: string
  schema?: Record<string, unknown>
}

export function pageMetadataTags(metadata: PageMetadata): Record<string, string | undefined> {
  const card = metadata.socialCard
  return {
    'name:description': metadata.description,
    'name:robots': metadata.robots,
    'name:googlebot': metadata.robots.includes('noindex') ? 'noindex,follow' : metadata.robots,
    'name:author': metadata.author,
    'property:og:url': metadata.canonical,
    'property:og:title': metadata.title,
    'property:og:description': metadata.description,
    'property:og:type': metadata.type ?? 'website',
    'property:og:image': card.url,
    'property:og:image:secure_url': card.url,
    'property:og:image:type': card.type,
    'property:og:image:width': card.width?.toString(),
    'property:og:image:height': card.height?.toString(),
    'property:og:image:alt': card.alt,
    'name:twitter:card': 'summary_large_image',
    'name:twitter:title': metadata.title,
    'name:twitter:description': metadata.description,
    'name:twitter:image': card.url,
    'name:twitter:image:alt': card.alt,
    'property:article:published_time': metadata.datePublished,
    'property:article:modified_time': metadata.dateModified,
    'property:article:author': metadata.authorUrl,
  }
}

export function readPageMetadata(doc: Document): PageMetadata | undefined {
  const canonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href')
  if (!canonical) return undefined
  const meta = (key: string) => doc.querySelector(`meta[${key}]`)?.getAttribute('content') ?? undefined
  const dimension = (value: string | undefined) => value && Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : undefined
  let schema: Record<string, unknown> | undefined
  try { schema = JSON.parse(doc.getElementById('route-schema')?.textContent ?? 'null') ?? undefined } catch { /* Invalid JSON-LD is replaced when page data is available. */ }
  return {
    canonical, title: doc.title, description: meta('name="description"') ?? '', robots: meta('name="robots"') ?? 'index,follow',
    type: meta('property="og:type"'), datePublished: meta('property="article:published_time"'), dateModified: meta('property="article:modified_time"'), author: meta('name="author"'), authorUrl: meta('property="article:author"'), schema,
    socialCard: {
      url: meta('property="og:image"') ?? DEFAULT_SOCIAL_CARD.url,
      alt: meta('property="og:image:alt"') ?? DEFAULT_SOCIAL_CARD.alt,
      type: meta('property="og:image:type"'), width: dimension(meta('property="og:image:width"')), height: dimension(meta('property="og:image:height"')),
    },
  }
}

export function applyPageMetadata(doc: Document, metadata: PageMetadata): void {
  doc.title = metadata.title
  for (const [key, value] of Object.entries(pageMetadataTags(metadata))) {
    const separator = key.indexOf(':')
    const attribute = key.slice(0, separator)
    const name = key.slice(separator + 1)
    const matches = [...doc.querySelectorAll<HTMLMetaElement>(`meta[${attribute}="${name}"]`)]
    if (value === undefined) { matches.forEach((node) => node.remove()); continue }
    const node = matches[0] ?? doc.createElement('meta')
    node.setAttribute(attribute, name)
    node.content = value
    if (!matches.length) doc.head.appendChild(node)
    matches.slice(1).forEach((duplicate) => duplicate.remove())
  }
  for (const language of [undefined, 'en-US', 'x-default']) {
    const selector = language ? `link[rel="alternate"][hreflang="${language}"]` : 'link[rel="canonical"]'
    const matches = [...doc.querySelectorAll<HTMLLinkElement>(selector)]
    const node = matches[0] ?? doc.createElement('link')
    node.rel = language ? 'alternate' : 'canonical'
    if (language) node.hreflang = language
    node.href = metadata.canonical
    if (!matches.length) doc.head.appendChild(node)
    matches.slice(1).forEach((duplicate) => duplicate.remove())
  }
  let script = doc.getElementById('route-schema') as HTMLScriptElement | null
  if (!script) { script = doc.createElement('script'); script.id = 'route-schema'; script.type = 'application/ld+json'; doc.head.appendChild(script) }
  script.textContent = JSON.stringify(metadata.schema ?? { '@context': 'https://schema.org', '@type': 'WebPage', name: metadata.title, url: metadata.canonical })
  const dataset = datasetSchemaForCanonical(metadata.canonical)
  let datasetScript = doc.getElementById('dataset-schema') as HTMLScriptElement | null
  if (!dataset) { datasetScript?.remove(); return }
  if (!datasetScript) { datasetScript = doc.createElement('script'); datasetScript.id = 'dataset-schema'; datasetScript.type = 'application/ld+json'; doc.head.appendChild(datasetScript) }
  datasetScript.textContent = JSON.stringify(dataset)
}
